import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface OrganisationUser {
  id: number;
  name: string;
  email: string;
  organisationId: number;
  role: string; // e.g., 'admin', 'manager', etc.
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: OrganisationUser | null | undefined; // Added undefined for loading state
  token: string | null;
  login: (token: string, user: OrganisationUser) => void;
  logout: () => void;
  isLoading: boolean; // Added to track loading state
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<OrganisationUser | null | undefined>(undefined); // Initialize as undefined (loading)
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true); // Start with loading true
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is already logged in
    const checkAuth = async () => {
      try {
        setIsLoading(true);
        const storedToken = localStorage.getItem('org_auth_token');
        const storedUser = localStorage.getItem('org_auth_user');
      
        console.log(storedToken);
        console.log(storedUser);
        
        if (storedToken && storedUser) {
          // Validate token if needed (could add API call here to validate token)
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
          setIsAuthenticated(true);
        } else {
          // If no token or user found, set user to null to indicate not authenticated
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Error checking authentication status:', error);
        // In case of any error, clear auth state
        setUser(null);
        setIsAuthenticated(false);
        localStorage.removeItem('org_auth_token');
        localStorage.removeItem('org_auth_user');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = (token: string, user: OrganisationUser) => {
    try {
      // Save to localStorage
      localStorage.setItem('org_auth_token', token);
      localStorage.setItem('org_auth_user', JSON.stringify(user));
      // Update state
      setToken(token);
      setUser(user);
      setIsAuthenticated(true);
      
      // Redirect to dashboard after successful login
      navigate('/dashboard');
    } catch (error) {
      console.error('Error during login:', error);
      logout(); // Clear any partial state in case of error
    }
  };

  const logout = () => {
    // Clear localStorage
    localStorage.removeItem('org_auth_token');
    localStorage.removeItem('org_auth_user');
    
    // Update state
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    
    // Navigate to login page
    navigate('/login');
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, token, login, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};